import { FC, useRef, useState, useCallback, MouseEventHandler, useEffect } from "react";
import _ from "lodash";

import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { useField } from "boomform";
import useFields from "./../../../../hooks/useFields";
import useLayout from "./../../../../hooks/useLayout";
import useShallowSelector from "./../../../../hooks/useShalowSelector";

import { IconTypes } from "./../../../../libs/Icon";
import Button from "./../../../../libs/Button";
import AutosizeInput from "react-input-autosize";
import ConditionList from "./ConditionList";
import ActionList from "./ActionList";

import { changeLogicPopup } from "./../../../../store/ui/actionCreators";
import { selectLogicPopup } from "./../../../../store/ui/selectors";

import { convertPosition } from "./../../../../helpers/logicPopup";

import { IRule } from "./../../../../store/logic/types";

import { updateLogicRequest } from "./../../../../store/logic/actionCreators";
import { selectRulesForList } from "./../../../../store/logic/selectors";

import { LogicDataType, orderFieldsForLogic } from "./../../../../helpers/orderFieldsForLogic";
import { getLabelPlaceholderDefault } from "./../../../../helpers/getLabelPlaceholderDefault";
import { getActiveRoute } from "./../../../../helpers/getActiveRoute";
import { FieldTypes } from "./../../../../store/fields/types";
import { RouteType } from "./../../../routes/types";

import styles from "./styles.module.scss";

const LogicPopup: FC = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const popupRef = useRef<HTMLDivElement | null>(null);
    const fieldIds = useFields().fields;
    const activeRoute = getActiveRoute(location.pathname);
    const { neededValues } = useField(["form.name", "fields"]);
    const layout = useLayout().layout;

    const { diffX, diffY, leftPosition, topPosition, currentRuleIndex } = useShallowSelector(selectLogicPopup);
    const rules: IRule[] = useShallowSelector(selectRulesForList).rules;
    const [isDragging, setIsDragging] = useState(false);
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);
    const [currentRule, setCurrentRule] = useState(
        rules[currentRuleIndex]
            ? _.cloneDeep(rules[currentRuleIndex])
            : {
                  name: "New Rule",
                  enabled: true,
                  conditions: [{ _id: "", condition: "", target_id: "", value: "", isValue: true }],
                  actions: [{ _id: "", action: "" }],
              },
    );

    const isLogicMode = getActiveRoute(location.pathname) === RouteType.Logic;

    useEffect(() => {
        setCurrentRule(
            rules[currentRuleIndex]
                ? _.cloneDeep(rules[currentRuleIndex])
                : {
                      name: "New Rule",
                      enabled: true,
                      conditions: [{ _id: "", condition: "", target_id: "", value: "", isValue: true }],
                      actions: [{ _id: "", action: "" }],
                  },
        );
        //eslint-disable-next-line
    }, [currentRuleIndex]);

    const handleMouseDown: MouseEventHandler = e => {
        const target = e.target as HTMLButtonElement;
        if (target.classList.contains("icon-close")) return false;

        const newDiffX = e.screenX - e.currentTarget.getBoundingClientRect().left;
        const newDiffY = e.screenY - e.currentTarget.getBoundingClientRect().top;

        dispatch(changeLogicPopup({ diffX: newDiffX, diffY: newDiffY }));
        setIsDragging(true);
    };

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!isDragging || !popupRef.current) return;
            const popupWidth = popupRef.current.clientWidth; // px
            const popupHeight = popupRef.current.clientHeight; // px
            const headerHeight = 64 + 44; // px

            const newLeftPos = e.screenX - diffX;
            const newTopPos = e.screenY - diffY;

            if (newLeftPos + popupWidth >= document.body.clientWidth)
                popupRef.current.style.left = `${document.body.clientWidth - popupWidth}px`;
            else popupRef.current.style.left = `${newLeftPos >= 0 ? newLeftPos : 0}px`;

            if (newTopPos + popupHeight >= document.body.clientHeight)
                popupRef.current.style.top = `${document.body.clientHeight - popupHeight}px`;
            else popupRef.current.style.top = `${newTopPos >= headerHeight ? newTopPos : headerHeight}px`;
        },
        [diffX, diffY, isDragging],
    );

    const handleMouseUp = () => {
        const top = popupRef.current?.getBoundingClientRect().top;
        const left = popupRef.current?.getBoundingClientRect().left;

        dispatch(changeLogicPopup({ leftPosition: left, topPosition: top }));
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            window.addEventListener("mouseup", handleMouseUp);
            document.body.addEventListener("mousemove", handleMouseMove);
        } else {
            window.removeEventListener("mouseup", handleMouseUp);
            document.body.removeEventListener("mousemove", handleMouseMove);
        }
        return () => {
            window.removeEventListener("mouseup", handleMouseUp);
            document.body.removeEventListener("mousemove", handleMouseMove);
        };
        // eslint-disable-next-line
    }, [isDragging, handleMouseMove]);

    useEffect(() => {
        setIsSaveDisabled(
            currentRule.conditions.some(condition => {
                return !(condition._id && condition.condition);
            }) ||
                currentRule.actions.some(action => {
                    return !(action._id && action.action);
                }),
        );
    }, [currentRule]);

    const handleLogicPopupClose = useCallback(() => {
        dispatch(changeLogicPopup({ isOpen: false }));
    }, [dispatch]);

    const handleSave = () => {
        rules[currentRuleIndex] = currentRule;
        rules[currentRuleIndex].name = currentRule.name;
        dispatch(updateLogicRequest({ rules: [...rules] }));
        dispatch(changeLogicPopup({ isOpen: false }));
    };

    if (activeRoute !== RouteType.Logic) return null;

    const conditionFields: LogicDataType[] = fieldIds
        .filter(field => {
            return (
                field.type !== FieldTypes.CUSTOM_HTML &&
                field.type !== FieldTypes.MAP &&
                field.type !== FieldTypes.HEADEING &&
                field.type !== FieldTypes.DESCRIPTION
            );
        })
        .map(field => {
            if (neededValues.fields) {
                const isLayout = layout[`${field._id}`]?.x && layout[`${field._id}`]?.y;
                return {
                    id: field._id,
                    type: field.type,
                    label: getLabelPlaceholderDefault({
                        label: neededValues.fields[`${field._id}`]?.label?.value,
                        placeholder: neededValues.fields[`${field._id}`]?.input?.data?.placeholder,
                        contentType: neededValues.fields[`${field._id}`]?.input?.data?.contentType,
                        isLabelHidden: neededValues.fields[`${field._id}`]?.label?.hidden,
                    }),
                    layoutWeight: isLayout
                        ? { x: layout[`${field._id}`].x!, y: layout[`${field._id}`].y! }
                        : { x: 0, y: 0 },
                };
            } else {
                return { id: "", label: "", type: "", layoutWeight: { x: 0, y: 0 } };
            }
        });

    const actionFields: LogicDataType[] = fieldIds.map(field => {
        if (neededValues.fields) {
            const isLayout = layout[`${field._id}`]?.x && layout[`${field._id}`]?.y;
            return {
                id: field._id,
                type: field.type,
                label: getLabelPlaceholderDefault({
                    label: neededValues.fields[`${field._id}`].label?.value,
                    placeholder: neededValues.fields[`${field._id}`]?.input?.data?.placeholder,
                    contentType: neededValues.fields[`${field._id}`]?.input?.data?.contentType,
                    isLabelHidden: neededValues.fields[`${field._id}`]?.label?.hidden,
                }),
                layoutWeight: isLayout
                    ? { x: layout[`${field._id}`].x!, y: layout[`${field._id}`].y! }
                    : { x: 0, y: 0 },
            };
        } else {
            return { id: "", label: "", type: "", layoutWeight: { x: 0, y: 0 } };
        }
    });
    const filteredActionFields = actionFields.filter(actionField => {
        return !currentRule.conditions.some(condition => condition._id === actionField.id);
    });
    const sortedConditionFields = orderFieldsForLogic(conditionFields);
    const sortedActionFields = orderFieldsForLogic(filteredActionFields);

    return currentRule && isLogicMode ? (
        <div
            className={styles.wrapper}
            ref={popupRef}
            style={{ left: leftPosition, top: convertPosition(topPosition, popupRef) }}
            draggable={true}
        >
            <div className={styles.header} onMouseDown={handleMouseDown}>
                <AutosizeInput
                    className={styles.ruleName}
                    value={currentRule.name}
                    onChange={change => {
                        setCurrentRule({ ...currentRule, name: change.target.value });
                    }}
                />

                <Button
                    className={styles.circle}
                    icon={IconTypes.CLOSE}
                    iconSize={24}
                    onClick={handleLogicPopupClose}
                />
            </div>

            <span className={styles.headerDivider}></span>

            <div className={styles.content}>
                <div className={styles.conditionAndIfDiv}>
                    <div className={styles.ifDiv}>
                        <div className={styles.ifBorder}>
                            <span className={styles.if}>If</span>
                        </div>
                        <span className={styles.ifThenConnection}></span>
                    </div>
                    <ConditionList
                        currentRuleIndex={currentRuleIndex}
                        fieldIds={fieldIds}
                        currentRule={currentRule}
                        sortedConditionFields={sortedConditionFields}
                        setCurrentRule={setCurrentRule}
                    />
                </div>

                <span className={styles.contentDivider}></span>
                <div className={styles.actionAndThenDiv}>
                    <div className={styles.thenDiv}>
                        <div className={styles.thenBorder}>
                            <span className={styles.then}>Then</span>
                        </div>
                        <span className={styles.thenIfConnection}></span>
                    </div>
                    <ActionList rule={currentRule} setRule={setCurrentRule} sortedActionFields={sortedActionFields} />
                </div>
            </div>
            <div className={styles.footer}>
                <Button text="Save" className={styles.saveBtn} onClick={handleSave} disabled={isSaveDisabled} />
            </div>
        </div>
    ) : null;
};

export default LogicPopup;
